import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { JobHeader } from '../components/jobs/details/JobHeader';
import { JobActions } from '../components/jobs/details/JobActions';
import { JobDescription } from '../components/jobs/JobDescription';
import { ApplicationForm } from '../components/jobs/ApplicationForm';
import { SkillsList } from '../components/jobs/SkillsList';
import { CourseRecommendationModal } from '../components/courses/CourseRecommendationModal';
import { getMissingSkills, getRecommendedCourses } from '../utils/courseRecommendations';
import { calculateSkillMatch } from '../utils/skillMatching';
import { mockCourses } from '../data/mockCourses';
import type { Job } from '../types/job';

export function JobDetailsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { skills } = useUser();
  const [showApplicationForm, setShowApplicationForm] = React.useState(false);
  const [isCourseModalOpen, setIsCourseModalOpen] = React.useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadJob = async () => {
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('*')
          .eq('id', jobId)
          .single();

        if (error) throw error;
        // Transform snake_case to camelCase
        setJob({
          ...data,
          technicalSkills: data.technical_skills || [],
          softSkills: data.soft_skills || [],
          postedAt: data.posted_at
        });
      } catch (error) {
        console.error('Error loading job:', error);
        toast.error('Failed to load job details');
        navigate('/jobs');
      } finally {
        setIsLoading(false);
      }
    };

    loadJob();
  }, [jobId, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-dark py-8">
        <div className="max-w-7xl mx-auto px-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Calculate skills and matches
  const userSkills = skills.map(s => s.name);
  const allJobSkills = job ? 
    [...(job.technicalSkills || []), ...(job.softSkills || [])] : 
    [];
  const missingSkills = getMissingSkills(userSkills, allJobSkills);
  const recommendedCourses = getRecommendedCourses(missingSkills, mockCourses);
  const matchPercentage = job ? calculateSkillMatch(userSkills, job.technicalSkills, job.softSkills) : 0;

  if (!job) {
    return (
      <div className="min-h-screen bg-background-dark py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-6">
            <Link
              to="/jobs"
              className="inline-flex items-center text-accent-green hover:opacity-80"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Job Board
            </Link>
          </div>
          <div className="bg-background-card rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Job Not Found</h2>
            <p className="text-gray-400 mb-6">The job listing you're looking for doesn't exist or has been removed.</p>
            <Link 
              to="/jobs" 
              className="btn-primary inline-flex"
            >
              Browse All Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/jobs"
            className="inline-flex items-center text-accent-green hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Job Board
          </Link>
        </div>

        <div className="bg-background-card rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <JobHeader job={job} matchPercentage={matchPercentage} />
            <div className="mb-6">
              <SkillsList
                technicalSkills={job.technicalSkills}
                softSkills={job.softSkills}
              />
            </div>
            <JobActions
              onApply={() => setShowApplicationForm(true)}
              onUpskill={() => navigate('/courses/marketplace')}
            />
          </div>

          <div className="border-t border-gray-800">
            <div className="p-6">
              <JobDescription
                description={job.description}
                requirements={job.requirements}
                responsibilities={job.responsibilities}
                benefits={job.benefits}
              />
            </div>
          </div>
        </div>

        {showApplicationForm && (
          <ApplicationForm
            job={job}
            onClose={() => setShowApplicationForm(false)} 
            onSuccess={() => {
              setShowApplicationForm(false);
              toast.success('Application submitted successfully');
            }}
          />
        )}

        <CourseRecommendationModal
          isOpen={isCourseModalOpen}
          onClose={() => setIsCourseModalOpen(false)}
          jobTitle={job.title}
          courses={recommendedCourses}
          missingSkills={missingSkills}
        />
      </div>
    </div>
  );
}